import client from './client';

/**
 * 解析 Content-Disposition header 拿文件名。
 *
 * 优先级:
 * 1. filename*=UTF-8''<urlencoded> (RFC 5987, 支持中文)
 * 2. filename="<ascii>" (老浏览器兼容)
 * 3. 返回 null (caller 用 fallback)
 *
 * 例:
 *   Content-Disposition: attachment; filename*=UTF-8''<urlencoded>.pdf; filename="report.pdf"
 *   → 解析返回 'AI胜任力测评报告（基础版）.pdf'
 */
export function parseContentDispositionFilename(header) {
  if (!header) return null;
  // 优先 filename* (RFC 5987)
  const utf8Match = header.match(/filename\*\s*=\s*([^']+)''([^;]+)/i);
  if (utf8Match) {
    const charset = utf8Match[1].toUpperCase();
    if (charset === 'UTF-8') {
      try { return decodeURIComponent(utf8Match[2].trim().replace(/^"|"$/g, '')); }
      catch (_) { /* fall through */ }
    }
  }
  // fallback 到 filename=
  const asciiMatch = header.match(/filename\s*=\s*"?([^";]+)"?/i);
  if (asciiMatch) return asciiMatch[1].trim();
  return null;
}

/**
 * 后端机器错误码 → 用户可读文案。未知/已是人话的 detail 原样返回。
 *
 * report_too_large：班级/企业 PDF 的 pre-flight 人数上限（后端
 * settings.TEAM_PDF_MAX_MEMBERS，env 可配），裸码不能直接给用户看。
 */
export function friendlyDownloadError(detail) {
  if (detail === 'report_too_large') return '团队人数超过报告生成上限，请联系平台管理员';
  return detail;
}

/**
 * 通用 blob PDF 下载 helper。
 *
 * 发起 GET 请求（responseType: 'blob', timeout: 120s），解析 Content-Disposition
 * 文件名，创建 <a> 触发浏览器下载，并解析 Blob 错误体中的 detail。
 *
 * @param {string} endpoint  - 传给 client.get() 的路径（不含 /api 前缀）
 * @param {object} options
 * @param {object} [options.params]       - query params
 * @param {string} [options.fallbackName] - Content-Disposition 缺失时的默认文件名
 * @returns {Promise<{ok: boolean, error?: string}>}
 */
export async function downloadBlobReport(endpoint, { params, fallbackName } = {}) {
  try {
    const resp = await client.get(endpoint, { responseType: 'blob', params, timeout: 120000 });
    const blob = new Blob([resp.data], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const cd = resp.headers['content-disposition'] || resp.headers['Content-Disposition'];
    a.download = parseContentDispositionFilename(cd) || fallbackName || '报告.pdf';
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
    return { ok: true };
  } catch (err) {
    let detail = null;
    const d = err?.response?.data;
    if (d instanceof Blob) { try { detail = JSON.parse(await d.text())?.detail || null; } catch (_) {} }
    else if (d && typeof d === 'object') { detail = d.detail || null; }
    return { ok: false, error: friendlyDownloadError(detail) || err?.message || 'PDF 下载失败' };
  }
}
